import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { hasSupabaseConfig } from './supabaseClient';
import QRRedirect from './components/QRRedirect';

// Main App Components
import BloodDrawForm from './BloodDrawForm';

// Test Directory Components
import TestDirectory from './components/TestDirectory';
import TestDetail from './components/TestDetail';

// Lab-specific Test Directory Components
import LabTestDetail from './components/LabTestDetail';
import LabRouteHandler from './components/LabRouteHandler';
import LabTestDirectory from './components/LabTestDirectory';

// LabAide collection-requirements lookup (tube / specimen / volume)
import LabAideLookup from './components/LabAideLookup';

// Secure Document Components
import PasswordRequest from './components/PasswordRequest';
import SecureDocument from './components/SecureDocument';

// Landing and shared components
import NewLandingPage from './components/NewLandingPage';

// Pitch Deck Components
import SecurePitchDeck from './components/SecurePitchDeck';

// Lab Directory Components
import LabDirectory from './components/LabDirectory';
import LabProfile from './components/LabProfile';
import LabSignup from './components/LabSignup';
import LabCheckout from './components/LabCheckout';
import LabSignupSuccess from './components/LabSignupSuccess';
import LabAgreement from './components/LabAgreement';

// Dynamic Lab Signup Component
import DynamicLabSignup from './components/DynamicLabSignup';

// Patient Layout Components  
import PatientLayout from './layouts/PatientLayout';
import BookingForm from './components/patient/BookingForm';
import PhlebotomistList from './components/patient/PhlebotomistList';
import PhlebotomistProfile from './components/patient/PhlebotomistProfile';

// Phlebotomist Layout Components
import PhlebotomistLayout from './layouts/PhlebotomistLayout';
import PhlebotomistDashboard from './components/phlebotomist/PhlebotomistDashboard';
// Removed NewDrawForm - using comprehensive BloodDrawForm instead
import WorkingHoursForm from './components/phlebotomist/WorkingHoursForm';
import AgreementSign from './components/phlebotomist/AgreementSign';
import LabDrawSummary from './components/phlebotomist/LabDrawSummary';
import BulkUpload from './components/phlebotomist/BulkUpload';
import RoutePlanner from './components/phlebotomist/RoutePlanner';
import PhlebotomistOnboarding from './components/phlebotomist/PhlebotomistOnboarding';
import SetPassword from './components/phlebotomist/SetPassword';
import PhlebDrawsView from './components/phlebotomist/PhlebDrawsView';
import PhlebPayslipsPage from './components/phlebotomist/PhlebPayslipsPage';
import PhlebSettings from './components/phlebotomist/PhlebSettings';
import PhlebTraining from './components/phlebotomist/PhlebTraining';

// Admin Routes
import AdminLayout from './layouts/AdminLayout';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import DailyTasks from './components/admin/DailyTasks';
import PhlebHours from './components/admin/PhlebHours';
import ReadyForDrop from './components/admin/ReadyForDrop';
import DropSpots from './components/admin/DropSpots';
import Inventory from './components/admin/Inventory';
import PhlebotomistManagement from './components/admin/PhlebotomistManagement';
import PatientManagement from './components/admin/PatientManagement';
import LabManagement from './components/admin/LabManagement';
import LabManagementGrid from './components/admin/LabManagementGrid';
import LabManagementCompact from './components/admin/LabManagementCompact';
import LabsPage from './components/admin/LabsPage';
import BillingManagement from './components/admin/BillingManagement';
import AdminUploadManager from './components/admin/AdminUploadManager';
import AgreementManagement from './components/admin/AgreementManagement';
import UnifiedLogin from './components/UnifiedLogin';
import AdminTestTypesUpload from './components/admin/AdminTestTypesUpload';
import SubmissionsList from './components/admin/SubmissionsList';
import SubmissionDetail from './components/admin/SubmissionDetail';
import SubmissionDetailPage from './components/admin/SubmissionDetailNew';
import BatchManagement from './components/admin/BatchManagement';
import InvoicesList from './components/admin/InvoicesList';
import InvoicesAndBatches from './components/admin/InvoicesAndBatches';
import DoctorManagement from './components/admin/DoctorManagement';
import FacilityManagement from './components/admin/FacilityManagement';
import AdminRoutePlanner from './components/admin/AdminRoutePlanner';
import AdminActiveRoutes from './components/admin/AdminActiveRoutes';
import AdminDuplicates from './components/admin/AdminDuplicates';
import LdxControls from './components/admin/LdxControls';
import SecretKeyDebugger from './components/admin/SecretKeyDebugger';
import AdminManagement from './components/admin/UserManagement';
import InsuranceManagement from './components/admin/InsuranceManagement';
import DropOffLocations from './components/admin/DropOffLocations';
import BloodDrawDashboard from './components/admin/BloodDrawDashboard';
import LabTestCostsManagement from './components/admin/LabTestCostsManagement';
import PitchDeckAnalytics from './components/admin/PitchDeckAnalytics';
import FaxManagement from './components/admin/FaxManagement';
import TestOfferingsManagement from './components/admin/TestOfferingsManagement';
import LiveStats from './components/LiveStats';
import LiveStatsEmbed from './components/LiveStatsEmbed';

// Doctor Agreement
import DoctorAgreementSign from './components/DoctorAgreementSign';
import FacilityAgreement from './components/FacilityAgreement';
import FacilitySignup from './components/FacilitySignup';
import FacilityLayout from './layouts/FacilityLayout';
import FacilityDashboard from './components/facility/FacilityDashboard';
import FacilityNewDraw from './components/facility/FacilityNewDraw';
import FacilityOrderEntry from './components/facility/FacilityOrderEntry';
import FacilityPatients from './components/facility/FacilityPatients';
import FacilitySubmissions from './components/facility/FacilitySubmissions';
import FacilityBulkUpload from './components/facility/FacilityBulkUpload';
import FacilityBulkUploadWizard from './components/facility/FacilityBulkUploadWizard';

// Doctor Portal Routes
import DoctorLayout from './layouts/DoctorLayout';
import DoctorLogin from './components/doctor/DoctorLogin';
import DoctorDashboard from './components/doctor/DoctorDashboard';
import DoctorAnalytics from './components/doctor/DoctorAnalytics';
import DoctorOrderEntry from './components/doctor/DoctorOrderEntry';

// Short URL Redirect
import ShortUrlRedirect from './components/ShortUrlRedirect';

// Lab Portal Routes
import LabPortalLayout from './layouts/LabPortalLayout';
import LabPortalLogin from './components/lab-portal/LabPortalLogin';
import LabPortalDashboard from './components/lab-portal/LabPortalDashboard';
import LabNewDraw from './components/lab-portal/LabNewDraw';
import LabTestCosts from './components/lab-portal/LabTestCosts';
import OnboardingGuide from './components/lab-portal/OnboardingGuide';
import QuickStart from './components/lab-portal/QuickStart';
import TestOnboarding from './components/lab-portal/TestOnboarding';
import BrandedLabPortal from './components/lab-portal/BrandedLabPortal';

// Payment
import PaymentUpdateStatus from './components/PaymentUpdateStatus';
import UpdatePaymentPage from './components/UpdatePaymentPage';
import PublicInvoicePage from './components/PublicInvoicePage';
import PublicInvoicePreview from './components/PublicInvoicePreview';
import LabInvoicesPage from './components/LabInvoicesPage';
import LabInvoicesPreview from './components/LabInvoicesPreview';
import InvoiceDetailPreview from './components/InvoiceDetailPreview';
import PatientPaymentPage from './components/PatientPaymentPage';
import PatientPaymentPreview from './components/PatientPaymentPreview';
// import PhlebotomistSignup from './components/PhlebotomistSignup';

// Shared Components


// Add this component before the App component
function AdminRouteGuard({ children }) {
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  if (!adminUser.id) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function App() {
  return (
      <Router>
        {!hasSupabaseConfig && (
          <div
            style={{
              background: '#b45309',
              color: '#fff',
              padding: '8px 16px',
              textAlign: 'center',
              fontSize: '14px',
            }}
            role="alert"
          >
            Database not connected. In Vercel: Settings → Environment Variables → add{' '}
            <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code>, then redeploy.
          </div>
        )}
        <Routes>
        {/* Public Routes */}
          <Route path="/" element={<NewLandingPage />} />
          <Route path="/login" element={<UnifiedLogin />} />
        <Route path="/home" element={<NewLandingPage />} />
        {/* Pitch Deck Route */}
        <Route path="/pitch-deck" element={<SecurePitchDeck />} />
        <Route path="/live-stats" element={<LiveStats />} />
        <Route path="/live-stats-embed" element={<LiveStatsEmbed />} />
        <Route path="/blood-draw" element={<BloodDrawForm />} />
        <Route path="/test-directory" element={<TestDirectory />} />
        <Route path="/test-directory/:testId" element={<TestDetail />} />

        {/* LabAide collection-requirements lookup (tube / specimen / volume) */}
        <Route path="/test-lookup" element={<LabAideLookup />} />
          
        {/* NEW: Lab Test Directory Routes */}
        <Route path="/lab-tests/:shortSlug" element={<LabTestDirectory />} />
        <Route path="/lab-tests/:shortSlug/test/:testId" element={<LabTestDetail />} />
        
        {/* Lab-specific Test Directory Routes */}
        <Route path="/lab/:identifier" element={<LabRouteHandler />} />
        <Route path="/lab/:shortSlug/test/:testId" element={<LabTestDetail />} />
          
          <Route path="/lab-directory" element={<LabDirectory />} />
          <Route path="/lab-directory/:labId" element={<LabProfile />} />
          
          {/* Dynamic Lab Signup Routes */}
          <Route path="/lab-signup/:labSlug" element={<DynamicLabSignup />} />
          
          {/* Generic Lab Signup (fallback) */}
          <Route path="/lab-signup" element={<LabSignup />} />
          <Route path="/lab-checkout" element={<LabCheckout />} />
          <Route path="/lab-agreement" element={<LabAgreement />} />
          <Route path="/lab-signup-success" element={<LabSignupSuccess />} />
          
        {/* Payment Update */}
        <Route path="/update-payment/:labId" element={<UpdatePaymentPage />} />
        <Route path="/payment-updated" element={<PaymentUpdateStatus />} />

        {/* Secure Document Routes */}
        <Route path="/Product-offering" element={<PasswordRequest />} />
        <Route path="/secure-document" element={<SecureDocument />} />
        <Route path="/doctor-agreement" element={<DoctorAgreementSign />} />
          <Route path="/doctor-agreement/:doctorId" element={<DoctorAgreementSign />} />
          <Route path="/agreement/facility/:facilityId" element={<FacilityAgreement />} />
          <Route path="/signup/facility" element={<FacilitySignup />} />
        {/* Public Invoice Pages (no login required) */}
        <Route path="/invoice-preview" element={<PublicInvoicePreview />} />
        <Route path="/invoice/:token" element={<PublicInvoicePage />} />
        <Route path="/invoices" element={<PublicInvoicePage />} />
        <Route path="/lab/:labId/invoices" element={<LabInvoicesPage />} />
        <Route path="/lab-invoices-preview" element={<LabInvoicesPreview />} />
        <Route path="/invoice-detail-preview/:mockId" element={<InvoiceDetailPreview />} />
        <Route path="/pay/:token" element={<PatientPaymentPage />} />
        <Route path="/patient-payment-preview" element={<PatientPaymentPreview />} />
        {/* <Route path="/signup/phlebotomist" element={<PhlebotomistSignup />} /> */}
        <Route path="/:shortCode" element={<ShortUrlRedirect />} />

        {/* Phlebotomist Routes — read-only portal: draws list, map, summary, hours.
             Upload/create/bulk routes intentionally removed; admins create draws. */}
        <Route path="/phlebotomist/training" element={<PhlebTraining />} />
        <Route path="/phlebotomist/onboard/:token" element={<PhlebotomistOnboarding />} />
        <Route path="/phlebotomist/reset-password" element={<SetPassword />} />
        <Route path="/phlebotomist/login" element={<UnifiedLogin />} />
        <Route path="/phlebotomist/:id/*" element={<PhlebotomistLayout />}>
          <Route index element={<PhlebDrawsView />} />
          <Route path="settings" element={<PhlebSettings />} />
          <Route path="working-hours" element={<WorkingHoursForm />} />
          <Route path="summary/:submissionId" element={<LabDrawSummary />} />
          <Route path="agreement" element={<AgreementSign />} />
          <Route path="route-planner" element={<RoutePlanner />} />
          <Route path="payslips" element={<PhlebPayslipsPage />} />
        </Route>

        {/* Legacy Phlebotomist Routes (without ID) */}
        <Route path="/phlebotomist/*" element={<PhlebotomistLayout />}>
          <Route index element={<PhlebDrawsView />} />
          <Route path="working-hours" element={<WorkingHoursForm />} />
          <Route path="summary/:submissionId" element={<LabDrawSummary />} />
          <Route path="agreement" element={<AgreementSign />} />
          <Route path="route-planner" element={<RoutePlanner />} />
        </Route>

          {/* Patient Routes */}
          <Route path="/patient/*" element={<PatientLayout />}>
          <Route index element={<BookingForm />} />
          <Route path="phlebotomists" element={<PhlebotomistList />} />
          <Route path="phlebotomist/:phlebotomistId" element={<PhlebotomistProfile />} />
            <Route path="patient/:patientEmail" element={<BloodDrawForm isPatientMode={true} />} />
            <Route path="summary/:submissionId" element={<LabDrawSummary />} />
            <Route path="agreement" element={<AgreementSign />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/*"
            element={
              <AdminRouteGuard>
                <AdminLayout />
              </AdminRouteGuard>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="today" element={<DailyTasks />} />
            <Route path="phleb-hours" element={<PhlebHours />} />
            <Route path="ready-for-drop" element={<ReadyForDrop />} />
            <Route path="drop-spots" element={<DropSpots />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="users" element={<AdminManagement />} />
            <Route path="phlebotomists" element={<PhlebotomistManagement />} />
            <Route path="patients" element={<PatientManagement />} />
            <Route path="labs" element={<LabsPage />} />
            <Route path="labs/upload" element={<LabManagement />} />
            <Route path="billing" element={<BillingManagement key="billing" initialTab="invoices" />} />
            {/* Invoices/batches now live as a tab under the Billing page. The
                distinct key remounts BillingManagement so the Invoices tab is
                preselected when arriving via these routes. */}
            <Route path="invoices" element={<BillingManagement key="invoices" initialTab="invoices" />} />
            <Route path="batches" element={<BillingManagement key="batches" initialTab="invoices" />} />
            <Route path="test-directory-uploads" element={<AdminUploadManager />} />
            <Route path="agreements" element={<AgreementManagement />} />
            <Route path="test-types" element={<AdminTestTypesUpload />} />
            <Route path="submissions" element={<SubmissionsList />} />
            <Route path="submissions/:id" element={<SubmissionDetailPage />} />
            <Route path="submissions-old/:id" element={<SubmissionDetail />} />
            <Route path="insurances" element={<InsuranceManagement />} />
            <Route path="drop-off-locations" element={<DropOffLocations />} />
            <Route path="blood-draws" element={<BloodDrawDashboard />} />
            <Route path="debug-secret-key" element={<SecretKeyDebugger />} />
            <Route path="lab-test-costs/:labId" element={<LabTestCostsManagement />} />
            <Route path="lab-test-costs" element={<LabTestCostsManagement />} />
            <Route path="test-offerings" element={<TestOfferingsManagement />} />
            <Route path="doctors" element={<DoctorManagement />} />
            <Route path="facilities" element={<FacilityManagement />} />
            {/* Route Planner hidden for now — redirect to dashboard.
                Restore: element={<AdminRoutePlanner />} */}
            <Route path="route-planner" element={<Navigate to="/admin" replace />} />
            <Route path="active-routes" element={<AdminActiveRoutes />} />
            <Route path="duplicates" element={<AdminDuplicates />} />
            <Route path="lab-api" element={<LdxControls />} />
            {/* Pitch Deck Analytics */}
            <Route path="pitch-deck-analytics" element={<PitchDeckAnalytics />} />
            {/* Fax Management */}
            <Route path="fax" element={<FaxManagement />} />
          </Route>

          {/* Lab Portal Routes */}
          <Route path="/lab-portal/*" element={<LabPortalLayout />}>
            <Route path="login" element={<LabPortalLogin />} />
            <Route path="dashboard" element={<LabPortalDashboard />} />
            <Route path="new-draw" element={<LabNewDraw />} />
            <Route path="test-costs" element={<LabTestCosts />} />
            {/* Onboarding route removed */}
            <Route path="quick-start" element={<QuickStart />} />
            <Route path="test-onboarding" element={<TestOnboarding />} />
          </Route>

          {/* Branded Lab Portal Routes (outside of LabPortalLayout for custom branding) */}
          <Route path="/lab-portal/:slug" element={<BrandedLabPortal />} />

          {/* Facility Portal Routes */}
          <Route path="/facility/:id/*" element={<FacilityLayout />}>
            <Route index element={<FacilityDashboard />} />
            <Route path="new-draw" element={<FacilityOrderEntry />} />
            <Route path="new-draw-simple" element={<FacilityNewDraw />} />
            <Route path="patients" element={<FacilityPatients />} />
            <Route path="submissions" element={<FacilitySubmissions />} />
            <Route path="bulk-upload" element={<FacilityBulkUploadWizard />} />
            <Route path="bulk-upload-csv" element={<FacilityBulkUpload />} />
          </Route>

          {/* Doctor Portal Routes */}
          <Route path="/doctor/*" element={<DoctorLayout />}>
            <Route path="login" element={<DoctorLogin />} />
            <Route path="dashboard" element={<DoctorDashboard />} />
            <Route path="analytics" element={<DoctorAnalytics />} />
            <Route path="order-entry" element={<DoctorOrderEntry />} />
          </Route>

          {/* QR Code Redirect Route */}
          <Route path="/qr/:qrId" element={<QRRedirect />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
  );
}

export default App;
